import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Copy, Check, ExternalLink, ArrowRight, Link as LinkIcon, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BookingWidgetSettings() {
  const navigate = useNavigate();
  const [slug, setSlug] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/settings`, { withCredentials: true });
        setSlug(res.data?.booking_slug || '');
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const bookingUrl = slug ? `${window.location.origin}/book/${slug}` : '';
  const copyUrl = async () => {
    if (!bookingUrl) return;
    try {
      await navigator.clipboard.writeText(bookingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error(e);
    }
  };

  return (
    <div className="bg-surface rounded-lg border border-border p-6" data-testid="booking-widget-settings-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-medium text-foreground">Booking Widget</h2>
          <p className="text-sm text-text-secondary">
            Your public booking page and embed code.
          </p>
        </div>
        <button
          onClick={() => navigate('/settings/booking-widget')}
          className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          data-testid="open-widget-setup"
        >
          Manage <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-text-secondary">Loading…</div>
      ) : slug ? (
        <div className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-text-muted mb-1">Your booking page</label>
            <div className="flex items-center gap-2 bg-surface-raised border border-border rounded-lg px-3 py-2">
              <LinkIcon className="w-4 h-4 text-text-muted flex-shrink-0" />
              <span className="flex-1 text-sm text-foreground font-mono truncate" data-testid="settings-booking-url">
                {bookingUrl}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={copyUrl}
              className="px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
              data-testid="settings-copy-url"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" /> Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" /> Copy link
                </>
              )}
            </button>
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 bg-surface-raised hover:bg-surface border border-border rounded-lg transition-colors flex items-center gap-2 text-sm font-medium text-foreground"
              data-testid="settings-preview-url"
            >
              <ExternalLink className="w-4 h-4" /> Preview
            </a>
            <button
              onClick={() => navigate('/settings/booking-widget')}
              className="sm:hidden px-3 py-1.5 bg-primary text-background rounded-lg text-sm font-medium flex items-center gap-1.5"
              data-testid="open-widget-setup-mobile"
            >
              Manage <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div
          className="bg-surface-raised border border-dashed border-border rounded-lg px-3 py-4 flex items-start gap-3"
          data-testid="settings-no-slug"
        >
          <AlertCircle className="w-5 h-5 text-primary/70 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-foreground font-medium mb-1">Set up your booking page</p>
            <p className="text-sm text-text-secondary mb-3">
              Pick a URL, grab the embed code, and start accepting clients from any website.
            </p>
            <button
              onClick={() => navigate('/settings/booking-widget')}
              className="px-3 py-1.5 bg-primary text-background rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-1.5"
              data-testid="cta-set-up-widget"
            >
              Get started <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
